import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from './../../../../environments/environment';
import { API } from '../../API';
import { FEDERATION_SLUG } from '../../../../main';

export interface Club {
  client_id: number;
  club_id: number;
  federation_id: number;
  club_code: string;
  club_name: string;
  Cle_CLUB: string;
  Statut_CLUB: 'ACTI' | 'INAC' | 'DESA';
  club_status?: 'ACTI' | 'INAC' | 'DESA';
  logo_club?: string;
}

export interface Federation {
  federation_id: number;
  federation_acornyme: string;
  federation_name: string;
  logo_url?: string;
}

export interface AuthResponse {
  success: boolean;
  user: {
    id: number;
    username: string;
    roles: string[];
    user_object_type: string;
    user_object_id?: number;
    permissions?: Record<string, string[]>;
  };
  club?: Club;
  federation?: Federation;
}

export interface User {
  id: number;
  username: string;
  roles: string[];
  user_object_type: string;
  user_object_id?: number;
  permissions?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private userDataSignal = signal<User | null>(null);
  private clubDataSignal = signal<Club | null>(null);
  private federationDataSignal = signal<Federation | null>(null);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  get federationSlug(): string | null {
    return FEDERATION_SLUG;
  }

  private clearOtherPlatformCache(platform: 'club' | 'fed'): void {
    if (platform === 'fed') {
      this.clubDataSignal.set(null);
    } else {
      this.federationDataSignal.set(null);
    }
  }

  login(email: string, password: string, user_object_type: 'FEDE' | 'CLUB' = 'CLUB'): Observable<AuthResponse> {
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}${API.AUTH.LOGIN}`, {
      email,
      password,
      federation_slug: this.federationSlug,
      user_object_type
    }, { withCredentials: true }).pipe(
      tap(response => {
        if (response.success) {
          this.clearOtherPlatformCache(user_object_type === 'FEDE' ? 'fed' : 'club');
          this.setUser(response.user);
          if (response.club) this.setClub(response.club);
          if (response.federation) this.setFederation(response.federation);
          this.currentUserSubject.next(response.user);
        }
      })
    );
  }

  logout(): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}${API.AUTH.LOGOUT}`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearAuth()),
      catchError((error: any) => {
        this.clearAuth();
        return new Observable(observer => observer.complete());
      })
    );
  }

  refreshToken(): Observable<any> {
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}/auth/refresh`, {}, { withCredentials: true }).pipe(
      tap((response: any) => {
        if (response?.success && response.user) {
          this.setUser(response.user);
          if (response.club)       this.setClub(response.club);
          if (response.federation) this.setFederation(response.federation);
          this.currentUserSubject.next(response.user);
        }
      })
    );
  }

  tryRestoreSession(): Promise<void> {
    return new Promise(resolve => {
      this.refreshToken().subscribe({ next: () => resolve(), error: () => resolve() });
    });
  }

  clearLocalAuth(): void {
    this.userDataSignal.set(null);
    this.clubDataSignal.set(null);
    this.federationDataSignal.set(null);
    this.currentUserSubject.next(null);
  }

  private clearAuth(): void {
    this.clearLocalAuth();
  }

  getClubStatusFromToken(): 'ACTI' | 'INAC' | 'DESA' | null {
    const club = this.getClub();
    if (!club) return null;
    const status = club.club_status || club.Statut_CLUB || null;
    if (status === 'ACTI' || status === 'INAC' || status === 'DESA') return status;
    return null;
  }

  getAccessLevel(): 'full' | 'readonly' | 'blocked' {
    if (!this.isAuthenticated()) return 'readonly';
    if (!this.isClub()) return 'full';
    const status = this.getClubStatusFromToken();
    if (status === 'ACTI') return 'full';
    if (status === 'INAC') return 'readonly';
    if (status === 'DESA') return 'blocked';
    return 'readonly';
  }

  setUser(user: User): void { this.userDataSignal.set(user); }
  getUser(): User | null { return this.userDataSignal(); }

  isAuthenticated(): boolean { return this.currentUserSubject.getValue() !== null; }
  isClub(): boolean { return this.getUser()?.user_object_type === 'CLUB'; }
  isFederation(): boolean { return this.getUser()?.user_object_type === 'FEDE'; }

  getRoles(): string[] { return this.getUser()?.roles ?? []; }
  isArbitre(): boolean { return this.getRoles().includes('FED_REFER'); }
  isDelegue(): boolean { return this.getRoles().includes('FED_DELEG'); }

  getPlatform(): 'fed' | 'club' | null {
    const type = this.getUser()?.user_object_type;
    if (type === 'FEDE') return 'fed';
    if (type === 'CLUB') return 'club';
    return null;
  }

  getHomeRoute(): string {
    if (this.isFederation()) return '/federation';
    if (this.isClub()) return '/club';
    return '/federation/auth/login';
  }

  setClub(club: Club): void { this.clubDataSignal.set(club); }
  getClub(): Club | null { return this.clubDataSignal(); }
  getClubId(): number | null { return this.getClub()?.club_id ?? null; }

  setFederation(federation: Federation): void { this.federationDataSignal.set(federation); }
  getFederation(): Federation | null { return this.federationDataSignal(); }
}
